import React, { FC } from 'react';
import { Box, Flex, Text, Card } from 'rebass/styled-components';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { AdminStats, formatBytes } from 'hooks/useAdminStats';

const CATEGORY_ORDER = ['master', 'preview', 'distribution', 'CAD'];
const CATEGORY_LABEL: Record<string, string> = {
  master: 'Master',
  preview: 'Preview',
  distribution: 'Distribution',
  CAD: 'Approval',
};

/** A figure. With `onOpen` it becomes a button that opens the matching filtered project list. */
const Stat: FC<{ label: string; value: string | number; sub?: React.ReactNode; accent?: string; onOpen?(): void; minHeight?: number }> = ({
  label,
  value,
  sub,
  accent,
  onOpen,
  minHeight,
}) => {
  const body = (
    <>
      <Text fontSize={0} color="grey3" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Text>
      <Text fontSize={4} fontWeight={2} color={accent || 'greyDark'} mt={1}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Text>
      {sub && (
        <Text fontSize={0} color="grey3" mt={1}>
          {sub}
        </Text>
      )}
    </>
  );
  if (!onOpen) {
    return (
      <Card p={3} bg="white" sx={{ boxShadow: 'main', borderRadius: 4, minWidth: 170, flex: '1 1 170px', minHeight }}>
        {body}
      </Card>
    );
  }
  return (
    <button
      type="button"
      className="archy-btn"
      onClick={onOpen}
      title="Show these projects"
      style={{
        padding: 16,
        background: '#FFFFFF',
        boxShadow: '0 0 15px rgba(0, 0, 0, 0.15)',
        borderRadius: 4,
        minWidth: 170,
        flex: '1 1 170px',
        textAlign: 'left',
        font: 'inherit',
        border: '2px solid transparent',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#16181D')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'transparent')}
    >
      {body}
      <Text fontSize={0} color="primary" mt={1} sx={{ fontWeight: 600 }}>
        Show and fix →
      </Text>
    </button>
  );
};

const Section: FC<{ title: string; note?: string }> = ({ title, note, children }) => (
  <Box mb={4}>
    <Flex alignItems="baseline" mb={2} sx={{ gap: '12px' }}>
      <Text fontSize={3} fontWeight={1} color="greyDark">
        {title}
      </Text>
      {note && (
        <Text fontSize={0} color="grey3">
          {note}
        </Text>
      )}
    </Flex>
    {children}
  </Box>
);

const Row = ({ cells, header }: { cells: (string | number)[]; header?: boolean }) => (
  <Flex
    py={2}
    px={2}
    sx={{ borderBottom: '1px solid', borderColor: 'grey2', fontWeight: header ? 1 : 0 }}
    color={header ? 'grey3' : 'greyDark'}
    fontSize={header ? 0 : 1}
  >
    {cells.map((c, i) => (
      <Box key={i} flex={i === 0 ? 3 : 1} sx={{ textAlign: i === 0 ? 'left' : 'right' }}>
        {typeof c === 'number' ? c.toLocaleString() : c}
      </Box>
    ))}
  </Flex>
);

const Overview: FC<{ stats: AdminStats }> = ({ stats }) => {
  const dispatch = useDispatch();
  const pct = (n: number) => (stats.projects.total ? Math.round((n / stats.projects.total) * 100) : 0);
  const categories = [
    ...CATEGORY_ORDER.filter((c) => stats.videos.byCategory[c]),
    ...Object.keys(stats.videos.byCategory).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];
  // Storage tier follows the label since 5 Nov 2026: Preview is hot, every other label is cold.
  const coldBytes = Object.keys(stats.videos.byCategory)
    .filter((c) => c !== 'preview')
    .reduce((sum, c) => sum + (stats.videos.byCategory[c].bytes || 0), 0);

  return (
    <>
      <Section title="Projects">
        <Flex flexWrap="wrap" sx={{ gap: '16px' }}>
          <Stat label="Total projects" value={stats.projects.total} />
          <Stat
            label="Complete"
            value={stats.projects.complete}
            sub={`${pct(stats.projects.complete)}% of projects`}
            accent="green"
          />
          <Stat
            label="Incomplete"
            value={stats.projects.incomplete}
            sub={`${pct(stats.projects.incomplete)}% of projects`}
            accent="primary"
          />
          <Stat label="Records" value={stats.records.total} sub={`${stats.notes.toLocaleString()} notes`} />
        </Flex>
      </Section>

      <Section title="Storage" note="Calculated from the file sizes stored on each database record">
        <Flex flexWrap="wrap" sx={{ gap: '16px' }} mb={3}>
          {/* Hot and cold on two short lines; the card stays the size of its neighbours (Aaron, 5 Nov 2026). */}
          <Stat
            label="Total stored"
            value={formatBytes(stats.totalBytes)}
            accent="primary"
            sub={
              <span style={{ display: 'flex', flexDirection: 'column', gap: 1, lineHeight: '15px' }}>
                <span style={{ color: '#E5056A', fontWeight: 600 }} title="Previews, project assets and gallery: hot storage (Standard)">
                  ● Hot {formatBytes(Math.max(0, stats.totalBytes - coldBytes))}
                </span>
                <span style={{ color: '#1E4E8C', fontWeight: 600 }} title="Master, Approval and Distribution files: cold storage (Archive)">
                  ● Cold {formatBytes(coldBytes)}
                </span>
              </span>
            }
          />
          <Stat label="Video files" value={stats.videos.total} sub={formatBytes(stats.videos.bytes)} />
          <Stat label="Project assets" value={stats.assets.files} sub={formatBytes(stats.assets.bytes)} />
          <Stat label="Gallery" value={stats.gallery.files} sub={formatBytes(stats.gallery.bytes)} />
          <Stat label="Images" value={stats.images} sub="sizes not recorded" />
        </Flex>
        <Card p={2} bg="white" sx={{ boxShadow: 'main', borderRadius: 4 }}>
          <Row header cells={['Video files by category', 'Files', 'Size', 'Share']} />
          {categories.map((c) => {
            const s = stats.videos.byCategory[c];
            return (
              <Row
                key={c}
                cells={[
                  CATEGORY_LABEL[c] || c,
                  s.files,
                  formatBytes(s.bytes),
                  `${stats.videos.bytes ? Math.round((s.bytes / stats.videos.bytes) * 100) : 0}%`,
                ]}
              />
            );
          })}
        </Card>
      </Section>

      <Section title="Largest projects" note="By storage used — click to open">
        <Card p={2} bg="white" sx={{ boxShadow: 'main', borderRadius: 4 }}>
          <Row header cells={['Project', 'Files', 'Size']} />
          {stats.topProjects.map((p) => (
            <Box
              key={p.id}
              onClick={() => dispatch(push(`/project/${p.id}`))}
              sx={{ cursor: 'pointer', ':hover': { bg: 'grey1' } }}
            >
              <Row cells={[`${p.title}${p.client ? ` — ${p.client}` : ''}`, p.files, formatBytes(p.bytes)]} />
            </Box>
          ))}
        </Card>
      </Section>

      <Section title="Needs attention" note="Click one to open that list of projects and work through it">
        <Flex flexWrap="wrap" sx={{ gap: '16px' }}>
          <Stat
            label="Incomplete projects"
            value={stats.projects.incomplete}
            accent="primary"
            onOpen={() => dispatch(push('/?attention=incomplete'))}
          />
          <Stat label="Projects with no client" value={stats.projects.noClient} onOpen={() => dispatch(push('/?attention=noClient'))} />
          <Stat
            label="Records with no video files"
            value={stats.records.withoutFiles}
            sub="Opens the projects they belong to"
            onOpen={() => dispatch(push('/?attention=emptyRecords'))}
          />
        </Flex>
      </Section>
    </>
  );
};

export default Overview;
